import { CalendarEntry } from "Components/Calendar/CalendarEntryHelper";
import { CellSize } from "Components/Calendar/CalendarHelper";
import { useIsIsoWeekdayEnabled } from "Components/CompanySetting/CompanySettingHelper";
import { DropArea } from "Components/DropArea/types/DropArea";
import { useIsInternalAdmin } from "Components/Members/hooks/useIsInternalAdmin";
import { ResizePixelSet } from "Components/Resize/ResizeHelper";
import { ScheduleEntryKind } from "Components/ScheduleEntry/ScheduleEntryHelper";
import { useSetWorkingHours } from "Components/WorkingHours/helpers/useSetWorkingHours";
import {
	MERGED_CALENDAR_ENTRY_COLOR_HEX,
	getColorHexFromScheduleEntry,
} from "Helpers/ColorHelper";
import { Project, ScheduleEntry, ScheduleEntryType } from "domain-entities";
import moment, { Moment } from "moment";

export interface DayGroup {
	date: Moment;
	projectCalendarEntries: CalendarEntry[];
	typeCalendarEntries: CalendarEntry[];
}

export interface MergedCalendarEntry {
	calendarEntry: CalendarEntry;
	calendarEntries: CalendarEntry[];
}

export function getMonthScheduleEntries(
	scheduleEntries: ScheduleEntry[],
	startOfCalendar: Moment,
	endOfCalendar: Moment
) {
	return scheduleEntries.filter((scheduleEntry) => {
		const startTime = moment.unix(scheduleEntry.startTime);
		const endTime = moment.unix(scheduleEntry.endTime);

		return (
			startTime.isBefore(endOfCalendar) && endTime.isAfter(startOfCalendar)
		);
	});
}

export function getMonthSchedulePhasesResizePixelSet(
	startOfWeek: Moment,
	cellSize: CellSize,
	enabledWorkingDaysLength: number
) {
	const weekWidth = cellSize.month.dayWidth * enabledWorkingDaysLength;
	const dayWidth = cellSize.month.dayWidth;
	const offsetPixel = cellSize.month.nameWidth + cellSize.offset.left;
	const resizePixelSet: ResizePixelSet = {
		left: [],
		right: [],
	};

	for (let week = 0; week < cellSize.month.weeksLength; week++) {
		for (let day = 0; day < enabledWorkingDaysLength; day++) {
			const dateTime = startOfWeek.clone().add(week, "weeks").add(day, "days");

			resizePixelSet.left.push({
				pixel: offsetPixel + week * weekWidth + day * dayWidth,
				dateTime: dateTime.clone(),
			});

			resizePixelSet.right.push({
				pixel: offsetPixel + week * weekWidth + (day + 1) * dayWidth - 1,
				dateTime: dateTime.clone(),
			});
		}
	}

	return resizePixelSet;
}

function getMonthDayGroups(props: {
	startOfCalendar: Moment;
	endOfCalendar: Moment;
	calendarEntries: CalendarEntry[];
}) {
	const calendarDiff = props.endOfCalendar.diff(props.startOfCalendar, "days");
	const dayGroups = Array(calendarDiff)
		.fill(null)
		.map<DayGroup>((_, index) => ({
			date: props.startOfCalendar.clone().add(index, "days"),
			projectCalendarEntries: [],
			typeCalendarEntries: [],
		}));

	props.calendarEntries.forEach((calendarEntry) => {
		const startDate = moment.unix(calendarEntry.startTime).startOf("day");
		const startDiff = startDate.diff(props.startOfCalendar, "days");
		const endDate = moment.unix(calendarEntry.endTime).startOf("day");
		const endDiff = endDate.diff(props.startOfCalendar, "days");

		const fromIndex = Math.max(startDiff, 0);
		const toIndex = Math.min(endDiff, calendarDiff - 1);

		for (let index = fromIndex; index <= toIndex; index++) {
			if (calendarEntry.projectId) {
				dayGroups[index].projectCalendarEntries.push(calendarEntry);
			} else {
				dayGroups[index].typeCalendarEntries.push(calendarEntry);
			}
		}
	});

	return dayGroups;
}

function useGetMonthRowItemsByKind() {
	const setWorkingHours = useSetWorkingHours();
	const isIsoWeekdayEnabled = useIsIsoWeekdayEnabled();
	const isInternalAdmin = useIsInternalAdmin();

	return (dayGroups: DayGroup[], kind: ScheduleEntryKind) => {
		const mergedCalendarEntries: MergedCalendarEntry[] = [];
		const dropAreas: DropArea[] = [];
		let previousMergedCalendarEntry: MergedCalendarEntry | null = null;
		let previousDropArea: DropArea | null = null;

		dayGroups.forEach((dayGroup) => {
			const calendarEntries =
				kind === ScheduleEntryKind.PROJECT
					? dayGroup.projectCalendarEntries
					: dayGroup.typeCalendarEntries;
			const calendarEntriesIdString =
				getCalendarEntriesIdString(calendarEntries);

			const endTime = setWorkingHours(dayGroup.date.clone(), "end");
			const startTime = setWorkingHours(dayGroup.date.clone(), "start");

			if (calendarEntries.length) {
				previousDropArea = null;
				const firstCalendarEntry = calendarEntries[0];

				if (
					calendarEntriesIdString ===
					previousMergedCalendarEntry?.calendarEntry.id
				) {
					previousMergedCalendarEntry.calendarEntry.endTime = endTime.unix();
				} else {
					previousMergedCalendarEntry = {
						calendarEntry: {
							...firstCalendarEntry,
							id: calendarEntriesIdString,
							startTime: startTime.unix(),
							endTime: endTime.unix(),
						},
						calendarEntries,
					};

					mergedCalendarEntries.push(previousMergedCalendarEntry);
				}
			} else {
				previousMergedCalendarEntry = null;
				if (isInternalAdmin && isIsoWeekdayEnabled(endTime.isoWeekday())) {
					if (previousDropArea?.end.week() !== endTime.week()) {
						previousDropArea = null;
					}

					if (previousDropArea) {
						previousDropArea.end = endTime;
					} else {
						previousDropArea = {
							start: startTime,
							end: endTime,
							isPlaceholder: kind === ScheduleEntryKind.TYPE,
						};

						dropAreas.push(previousDropArea);
					}
				}
			}
		});

		return {
			mergedCalendarEntries,
			dropAreas,
		};
	};
}

export function useGetMonthRowItems() {
	const getMonthRowItemsByKind = useGetMonthRowItemsByKind();

	return (
		calendarEntries: CalendarEntry[],
		startOfCalendar: Moment,
		endOfCalendar: Moment
	) => {
		const dayGroups = getMonthDayGroups({
			startOfCalendar,
			endOfCalendar,
			calendarEntries,
		});

		const projectRowItems = getMonthRowItemsByKind(
			dayGroups,
			ScheduleEntryKind.PROJECT
		);

		const typeRowItems = getMonthRowItemsByKind(
			dayGroups,
			ScheduleEntryKind.TYPE
		);

		return {
			mergedCalendarEntries: [
				...projectRowItems.mergedCalendarEntries,
				...typeRowItems.mergedCalendarEntries,
			],
			dropAreas: [...projectRowItems.dropAreas, ...typeRowItems.dropAreas],
		};
	};
}

function getCalendarEntriesIdString(calendarEntries?: CalendarEntry[]) {
	if (calendarEntries) {
		return calendarEntries
			.map((calendarEntry) => calendarEntry.id)
			.sort((a, b) => b.localeCompare(a))
			.join(",");
	} else {
		return "";
	}
}

export function getMonthMergedCalendarEntryColorHex(
	mergedCalendarEntry: MergedCalendarEntry,
	projects: Project[],
	scheduleEntryTypes: ScheduleEntryType[]
) {
	if (mergedCalendarEntry.calendarEntries.length > 1) {
		return {
			colorHex: MERGED_CALENDAR_ENTRY_COLOR_HEX,
			colorHexes: mergedCalendarEntry.calendarEntries.map((calendarEntry) =>
				getColorHexFromScheduleEntry(
					calendarEntry,
					projects,
					scheduleEntryTypes
				)
			),
		};
	} else {
		return {
			colorHex: getColorHexFromScheduleEntry(
				mergedCalendarEntry.calendarEntry,
				projects,
				scheduleEntryTypes
			),
		};
	}
}

export function isMonthMergedCalendarEntryCutOff(
	mergedCalendarEntry: MergedCalendarEntry
) {
	if (mergedCalendarEntry.calendarEntries.length === 1) {
		const mergedCalendarEntryStartDate = moment
			.unix(mergedCalendarEntry.calendarEntry.startTime)
			.startOf("day");
		const mergedCalendarEntryEndDate = moment
			.unix(mergedCalendarEntry.calendarEntry.endTime)
			.startOf("day");
		const calendarEntryStartDate = moment
			.unix(mergedCalendarEntry.calendarEntries[0].startTime)
			.startOf("day");
		const calendarEntryEndDate = moment
			.unix(mergedCalendarEntry.calendarEntries[0].endTime)
			.startOf("day");

		return {
			left: !calendarEntryStartDate.isSame(mergedCalendarEntryStartDate),
			right: !calendarEntryEndDate.isSame(mergedCalendarEntryEndDate),
		};
	} else {
		return {
			left: false,
			right: false,
		};
	}
}
